import { PaymentMethodConditions, paymentMethodConditions } from '@nemuru-eng/checkout-js';
import { Currency, LenderName, Loan, MoneyDto, PercentDto } from '@nemuru-eng/js-api-client';
import { Insurance } from '@nemuru-eng/js-api-client/slim';

import { LoanConditionsGroup } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/ConditionsGroup/LoanConditionsGroup';
import {
  AbstractPaymentMethod,
  SetupOptions,
} from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractPaymentMethod';
import { NoConditionsAvailableException } from '@/components/modules/Application/components/PaymentMethod/_shared/exceptions/NoConditionsAvailableException';
import { LoanConditionsMap } from '@/components/modules/Application/components/PaymentMethod/_shared/maps/LoanConditionsMap';
import { Filter } from '@/components/modules/Application/components/PaymentMethod/_shared/types/Filter';
import { LoanConditions } from '@/components/modules/Application/components/PaymentMethod/_shared/types/LoanConditions';
import { LoanInsuranceMap } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LoanConditions/maps/loanInsurance.map';

export abstract class AbstractLoanPaymentMethod extends AbstractPaymentMethod {
  public filters: Filter[] | null = null;
  public selectedTerm: number | null = null;
  public allTerms: number[] = [];
  public paymentMethodConditions: PaymentMethodConditions[] | null = null;
  public conditionsGroups: LoanConditionsGroup[] = [];
  public selectedConditionsGroup: LoanConditionsGroup | null = null;
  protected _allConditions: LoanConditions[] = [];
  protected _availableConditions: LoanConditions[] = [];
  protected _options: SetupOptions | null = null;

  public abstract get proposedTerms(): number[];
  protected abstract setAvailableFilters(allConditions: LoanConditions[]): void;
  protected abstract getAvailableConditions(allConditions: LoanConditions[]): LoanConditions[];
  protected abstract getSelectedTermCandidate(availableTerms: number[]): number;
  protected abstract getConditionsGroups(availableConditions: LoanConditions[], term: number): LoanConditionsGroup[];
  protected abstract getSelectedConditionsGroupCandidate(conditionsGroups: LoanConditionsGroup[]): LoanConditionsGroup;

  public setup(amount: number, currency: Currency, options?: SetupOptions): void {
    this.amount = amount;
    this.currency = currency;
    this._options = options ?? null;

    const allConditions = this.getAllConditions(amount);
    this.allTerms = this.getAllTerms(allConditions);
    this.setAvailableFilters(allConditions);
    const availableConditions = this.getAvailableConditions(allConditions);
    this._allConditions = allConditions;
    this.prefetchProviderLogos();
    this._availableConditions = availableConditions;
    const availableTerms = this.availableTerms;
    const selectedTerm = this.getSelectedTermCandidate(availableTerms);
    const conditionsGroups = this.getConditionsGroups(availableConditions, selectedTerm);
    this.selectedTerm = selectedTerm;
    this.conditionsGroups = conditionsGroups;
    this.selectedConditionsGroup = this.getSelectedConditionsGroupCandidate(conditionsGroups);
    this.paymentMethodConditions = this.getPaymentMethodConditions(availableConditions);
    if (allConditions.length > 0) this.isSetup = true;
  }

  protected getAllConditions(amount: number): LoanConditions[] {
    const opts = this._options;
    const terms = opts?.terms && opts.terms.length > 0 ? opts.terms : undefined;

    const conditions = paymentMethodConditions({
      amount,
      paymentMethodType: this.paymentMethodType,
      ...(terms && { terms }),
    });

    this.guardConditionsExist(conditions);
    return LoanConditionsMap.toLoanConditions(conditions);
  }

  private guardConditionsExist(conditions: PaymentMethodConditions[]): void {
    if (!conditions.length || conditions.some((option) => !option.conditions)) {
      throw new NoConditionsAvailableException(this.paymentMethodType);
    }
  }

  public getAllTerms(allConditions: LoanConditions[]): number[] {
    const allTerms = [
      ...new Set(allConditions.map((condition) => condition.term).filter((term) => term !== undefined)),
    ] as number[];
    return allTerms.sort((a, b) => (a > b ? 1 : -1));
  }

  public get availableTerms(): number[] {
    const availableTerms = [
      ...new Set(this._availableConditions.map((condition) => condition.term).filter((term) => term !== undefined)),
    ] as number[];
    return availableTerms.sort((a, b) => (a > b ? 1 : -1));
  }

  public getActiveProviderNames(): LenderName[] {
    return this.selectedConditionsGroup ? this.selectedConditionsGroup.providerNames() : [];
  }

  public getAllProviderNames(): LenderName[] {
    // Prioritizing new simulated conditions if available
    if (this.paymentMethodConditions && this.paymentMethodConditions.length > 0) {
      return [
        ...new Set(
          this.paymentMethodConditions
            .map((condition) => condition.conditions?.lenderName)
            .filter((lenderName) => !!lenderName) as LenderName[],
        ),
      ];
    }

    return [
      ...new Set(
        this._allConditions
          .map((condition) => condition.providerName)
          .filter((providerName) => !!providerName) as LenderName[],
      ),
    ];
  }

  public prefetchProviderLogos(): void {
    this.getAllProviderNames().forEach((providerName) => {
      const img = new Image();
      img.src = `https://cdn.nemuru.com/assets/logos/letterform/${providerName}-logo.svg`;
    });
  }

  public updateSelectedTerm(term: number): this {
    const conditionsGroups = this.getConditionsGroups(this._availableConditions, term);
    this.selectedTerm = term;
    this.conditionsGroups = conditionsGroups;
    this.selectedConditionsGroup = this.getSelectedConditionsGroupCandidate(conditionsGroups);
    this.paymentMethodConditions = this.getPaymentMethodConditions(this.selectedConditionsGroup?.conditions ?? []);
    return this;
  }

  public updateFilters(filters: Filter[]): this {
    if (!this.selectedTerm) {
      throw new Error('Selected term is not set');
    }
    this.filters = filters;
    const availableConditions = this.getAvailableConditions(this._allConditions);
    this._availableConditions = availableConditions;
    const conditionsGroups = this.getConditionsGroups(availableConditions, this.selectedTerm);
    this.conditionsGroups = conditionsGroups;
    this.selectedConditionsGroup = this.getSelectedConditionsGroupCandidate(conditionsGroups);
    return this;
  }

  private getPaymentMethodConditions(conditions: LoanConditions[]): PaymentMethodConditions[] {
    return conditions.map(
      (condition) =>
        ({
          type: this.paymentMethodType,
          amount: condition.principal,
          currency: this.currency,
          conditions: {
            ...condition,
            lenderName: condition.providerName,
          },
        } as unknown as PaymentMethodConditions),
    );
  }

  public setConditionFromLoanConditions(loan: Loan, selectedLenders?: LenderName[]): void {
    const insurance = LoanInsuranceMap.fromLoan(loan) as Insurance | null;
    const loanConditions = loan.loanConditions;

    const loanPaymentMethodConditions = {
      type: this.paymentMethodType,
      amount: new MoneyDto({ value: loanConditions.principal.amount, currency: loanConditions.principal.currency }),
      currency: loanConditions.principal.currency,
      conditions: {
        product: loanConditions.product,
        program: loanConditions.program.name,
        ...(loanConditions.loanCalculator.term.period ? { term: loanConditions.loanCalculator.term.period } : {}),
        annualInterest: new PercentDto(loanConditions.annualInterest ?? 0),
        apr: new PercentDto(loanConditions.loanCalculator.tae),
        setupFeeType: loanConditions.loanCalculator.setupFee.type ?? undefined,
        ...(loanConditions.loanCalculator.setupFee.feeAmount?.amount
          ? {
              setupFeeAmount: new MoneyDto({
                value: loanConditions.loanCalculator.setupFee.feeAmount?.amount ?? 0,
                currency: loanConditions.principal.currency,
              }),
            }
          : {}),
        ...(loanConditions.loanCalculator.gracePeriod
          ? { gracePeriod: loanConditions.loanCalculator.gracePeriod }
          : {}),
        instalmentTotalAmount: new MoneyDto({
          value: loanConditions.loanCalculator.userDefinedQuota?.amount ?? 0,
          currency: loanConditions.principal.currency,
        }),
        totalAmount: new MoneyDto({
          value: loanConditions.loanCalculator.totalAmountPaid?.amount ?? 0,
          currency: loanConditions.principal.currency,
        }),
        totalInstalmentFeeAmount: new MoneyDto({
          value:
            (loanConditions.loanCalculator.totalAmountPaid?.amount ?? 0) -
            (loanConditions.loanCalculator.totalInterestPaid?.amount ?? 0) -
            loanConditions.principal.amount,
          currency: loanConditions.principal.currency,
        }),
        totalInterestAmount: new MoneyDto({
          value: loanConditions.loanCalculator.totalInterestPaid?.amount ?? 0,
          currency: loanConditions.principal.currency,
        }),
        lenderName: undefined,
        ...(insurance && { insurance }),
      },
    };

    if (selectedLenders && selectedLenders.length > 0) {
      this.paymentMethodConditions = [...selectedLenders].map((lenderName) => ({
        ...loanPaymentMethodConditions,
        conditions: {
          ...loanPaymentMethodConditions.conditions,
          lenderName,
        },
      }));
    } else {
      this.paymentMethodConditions = [loanPaymentMethodConditions];
    }

    // TODO: Review this
    this.setSelectedConditionsGroup(LoanConditionsGroup.fromSimulatorConditions(this.paymentMethodConditions[0]));
  }

  public get allConditions(): LoanConditions[] {
    return this._allConditions;
  }

  public get availableConditions(): LoanConditions[] {
    return this._availableConditions;
  }
}
